import { Autocomplete, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getAllNotRecognizedPayments,
  getNotRecognizedPayment,
} from '../../../../../../lib/api';
import DocumentSearcher from '../../../../../../lib/common/DocumentSearcher';
import CustomAlert from '../../../../../../lib/designSystem/Alert';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../../lib/designSystem/HeaderWithActions';
import EnhancedTable from '../../../../../../lib/designSystem/Table';
import TableWithPagination from '../../../../../../lib/designSystem/TableWithPagination';
import {
  hasAnyPermissions,
  showAlert,
} from '../../../../../../lib/functions/utils';
import { AuthState } from '../../../../../../services/redux/authSlice';
import NotRecognizedPaymentDetails from './components/NotRecognizedPaymentDetails';
import ReverseFNCDialog from './components/ReverseFNCDialog';


const headers: { key: string; label: string; type?: any }[] = [
  { key: 'id', label: 'ID', type: 'numeric-id' },
  { key: 'stakeholderName', label: 'Origen' },
  { key: 'stakeholderIdentifier', label: 'RUT' },
  { key: 'createdAt', label: 'Fecha', type: 'date' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'status', label: 'Estado', type: 'conciliation-status' },
];

const FNC: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [notRecognizedPayments, setNotRecognizedPayments] = useState<any[]>([]);
  const [notRecognizedPayment, setNotRecognizedPayment] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [status, setStatus] = useState('PENDING');
  const [fncForReverseId, setFNCForReverseId] = useState('');
  const [openFNCReverse, setOpenFNCReverse] = useState(false);

  const statusOptions = [
    { label: 'Pendiente', value: 'PENDING' },
    { label: 'Conciliado', value: 'CONCILIATED' },
  ];
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (status) query += `status=${status}&`;
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const fetchFNC = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      const query = generateQuery({ page, pageSize });
      const response = await getAllNotRecognizedPayments(dispatch, query);
      setNotRecognizedPayments(response.data);
      setPagination(response.pagination);
      return response;
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al cargar los FNCS');
    }
  };

  const setUp = async () => {
    setLoading(true);
    await fetchFNC({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  useEffect(() => {
    void setUp();
  }, []);

  const handleDetails = async (notRecognizedPaymentId: any, setUp?: any) => {
    setLoading(true);
    try {
      if (setUp) await fetchFNC({ page: 0, pageSize: PAGE_SIZE });
      const response = await getNotRecognizedPayment(
        dispatch,
        notRecognizedPaymentId,
      );
      setNotRecognizedPayment(response);
      setOpen(true);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al cargar los FNCs');
    }
    setLoading(false);
  };

  const handleReverse = (id: string) => {
    setFNCForReverseId(id);
    setOpenFNCReverse(true);
  };

  const canShowFNCAssign = () => {
    if (hasAnyPermissions(user, ['financing_fnc_assign'])) return true;
    return false;
  };

  const canShowReverse = () => {
    if (hasAnyPermissions(user, ['financing_fnc_reverse'])) return true;
    return false;
  };

  const actions: any = [
    {
      action: handleDetails,
      actionLabel: 'Detalles',
      canShow: canShowFNCAssign,
    },
    { action: handleReverse, actionLabel: 'Reversar', canShow: canShowReverse },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        variant="body1"
        title="Acá podrás ver los los pagos no reconocidos generados."
        buttons={[
          {
            name: 'Filtro',
            action: () => {},
            color: 'primary',
            customButton: (
              <DocumentSearcher
                searchData={fetchFNC}
                setData={setNotRecognizedPayments}
                setLoading={setLoading}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                stakeholderIdentifier={stakeholderIdentifier}
                setStakeholderIdentifier={setStakeholderIdentifier}
                pagination={pagination}
                status={status}
                setStatus={setStatus}
                statusOptions={statusOptions}
              />
            ),
          },
        ]}
      />
      <TableWithPagination
        dataHeaders={headers}
        searchData={fetchFNC}
        data={notRecognizedPayments}
        rowsPerPageDefault={50}
        actions={actions}
        exportToFile="xlsx"
        lastElement
        setData={setNotRecognizedPayments}
        pagination={pagination}
        setPagination={setPagination}
        loading={loading}
        setLoading={setLoading}
      />
      {open && (
        <NotRecognizedPaymentDetails
          open={open}
          setOpen={setOpen}
          notRecognizedPayment={notRecognizedPayment}
          setNotRecognizedPayment={setNotRecognizedPayment}
          setUp={fetchFNC}
          handleDetails={handleDetails}
        />
      )}
      {openFNCReverse && (
        <ReverseFNCDialog
          open={openFNCReverse}
          setOpen={setOpenFNCReverse}
          fncForReverseId={Number(fncForReverseId)}
          bigSetUp={setUp}
        />
      )}
    </div>
  );
};

export default FNC;
